import { Button, Form, Input, Modal } from 'antd';
import dayjs from 'dayjs';
import customParseFormat from 'dayjs/plugin/customParseFormat';
import get from 'lodash/get';
import queryString from 'query-string';
import { useEffect, useState } from 'react';
import { withApollo } from 'react-apollo';
import { withRouter } from 'react-router-dom';
import { getEnvironment, graphql } from 'src/_shared/services/utils.js';

dayjs.extend(customParseFormat);

export const ClaimAccountModal = ({
	visible,
	closeModal,
	userSignupSettings,
	location,
	history,
	client,
	theme,
	labelEmployeeID,
}) => {
	const [form] = Form.useForm();
	const [state, setState] = useState({
		cognitoError: false,
		errorMessage: null,
		date: '',
		passedDOB: null,
		dontUseDateFormat: false,
		dobLabel: 'Date of Birth',
	});

	useEffect(() => {
		if (userSignupSettings) {
			const formattedUSS = JSON.parse(userSignupSettings);
			setState((prevState) => ({
				...prevState,
				dobLabel: formattedUSS.dobLabel || 'Date of Birth',
				dontUseDateFormat: formattedUSS.disableDOBDateFormat === true,
			}));
		}

		// Allow passing in DOB via query string. If it is passed in, hide the form field and set the value
		const queryParameters = queryString.parse(location.search);
		if (
			queryParameters &&
			queryParameters.claimDate &&
			!state.dontUseDateFormat
		) {
			setState((prevState) => ({
				...prevState,
				passedDOB: queryParameters.claimDate,
			}));
		}
	}, [userSignupSettings, location.search, state.dontUseDateFormat]);

	const onChangeDate = (e) => {
		const input = e.target.value ? e.target.value : '';
		if (input.length > 10) return state.date;
		if (input.length <= state.date.length) {
			let res = input;
			const lastChar = input.slice(-1, input.length);
			if (!containsSpecialChars(lastChar)) {
				res = res.slice(0, -1);
			}

			setState((prevState) => ({ ...prevState, date: res }));
			return res;
		}

		setState((prevState) => ({ ...prevState, date: input }));
		let result = '';
		const allDates = input.split(/\D/g);
		let month = allDates[0] ? allDates[0] : '';
		if (month && clean(month).length < 2 && input.length >= 2) {
			month = '0' + month;
		}

		if (month) {
			result = month;
		}

		if (month.length === 2) {
			result = month + '/';
		}

		let day = allDates[1] ? allDates[1] : '';
		if (day) {
			result = month + '/' + day;
		}

		if (day && clean(day).length < 2 && input.length > 4) {
			day = '0' + day;
		}

		if (day.length === 2) {
			result = month + '/' + day + '/';
		}

		const year = allDates[2] ? allDates[2] : '';
		if (year) {
			result = month + '/' + day + '/' + year;
		}

		return result ? result : input;
	};

	const clean = (value) => {
		const value_ = value;
		const result = value_.replaceAll(/\D/g, '');
		return result;
	};

	const containsSpecialChars = (value) => {
		return /[~`!#$%^&*+=\-[\]\\';,/{}|":<>?]/g.test(value);
	};

	const formatDate = (input) => {
		const allDates = input.split(/\D/g);
		let month = allDates[0] ? allDates[0] : '';
		if (month.length < 2) {
			month = '0' + month;
		}

		let day = allDates[1] ? allDates[1] : '';
		if (day.length < 2) {
			day = '0' + day;
		}

		const year = allDates[2] ? allDates[2] : '';
		return `${month}/${day}/${year}`;
	};

	const handleSubmit = async (values) => {
		const configMode = getEnvironment() === 'dev' ? 'DEV' : 'PROD';
		try {
			let valuesDateOfBirth = formatDate(
				get(values, 'dateOfBirth', '00/00/0000')
			);

			if (state.passedDOB && !state.dontUseDateFormat) {
				valuesDateOfBirth = formatDate(state.passedDOB);
			}

			let formattedDOB = dayjs(valuesDateOfBirth).format('MM-DD-YYYY');
			if (state.dontUseDateFormat === true) {
				formattedDOB = get(values, 'dateOfBirth', '');
			}

			const employeeId = values.employeeId.toLowerCase();

			// START getting ACCOUNT CLAIM DATA
			let claim = '';
			try {
				claim = await graphql({
					input: {
						mode: configMode,
						firstInitialLastName: employeeId,
						dateOfBirth: formattedDOB,
					},
					query: 'getAccountClaimByFirstInitialLastNameDOB',
				});
			} catch (error) {
				console.log(error);
			}
			// END getting ACCOUNT CLAIM DATA

			const claimDateOfBirth = formatDate(
				get(claim, 'dateOfBirth', '00/00/0000')
			);
			const loginUser = await graphql({
				input: {
					accountClaim: JSON.stringify(claim),
					employeeId,
				},
				query: 'getLoginUser',
			});

			if (
				claim &&
				claimDateOfBirth === valuesDateOfBirth &&
				claim?.active &&
				!claim?.claimed &&
				!loginUser?.id
			) {
				const queryParameters = queryString.parse(location.search);
				const source = queryParameters?.source;
				history.push({
					pathname: `/newuser/${values.employeeId}`,
					search: source ? `?source=${source}` : '',
					state: {
						accountClaim: claim,
						employeeId: values.employeeId,
					},
				});
				return 'Success!';
			}

			if (!claim) {
				form.setFields([
					{
						name: 'employeeId',
						errors: [
							`No ${labelEmployeeID ? labelEmployeeID : 'Employee ID'} Found.`,
						],
					},
				]);

				return Promise.reject(
					`No ${labelEmployeeID ? labelEmployeeID : 'Employee ID'} Found.`
				);
			}

			if (claim?.claimed || claim?.userId || loginUser?.id) {
				form.setFields([
					{
						name: 'employeeId',
						errors: ['Account Already Claimed.'],
					},
				]);
				return Promise.reject('Account Already Claimed.');
			}

			if (!get(claim, 'active')) {
				form.setFields([
					{
						name: 'employeeId',
						errors: ['This Employee ID is disabled.'],
					},
				]);
				return Promise.reject('This Employee ID is disabled.');
			}

			form.setFields([
				{
					name: 'dateOfBirth',
					errors: ['Invalid Date of Birth.'],
				},
			]);
			return Promise.reject('Invalid Date of Birth.');
		} catch {
			form.setFields([
				{
					name: 'employeeId',
					errors: [
						`No ${labelEmployeeID ? labelEmployeeID : 'Employee ID'} Found`,
					],
				},
			]);

			return Promise.reject(
				`No ${labelEmployeeID ? labelEmployeeID : 'Employee ID'} Found`
			);
		}
	};

	const { cognitoError, errorMessage } = state;
	const dontUseDateFormat = Boolean(state.dontUseDateFormat);
	const dobLabel = state.dobLabel;

	return (
		<Modal
			destroyOnClose
			height={350}
			title="Claim Your Account"
			footer={null}
			open={visible}
			width={500}
			onCancel={closeModal}
			onOk={null}
		>
			<Form form={form} onFinish={handleSubmit}>
				<Form.Item
					name="employeeId"
					rules={[
						{
							type: 'string',
						},
						{
							required: true,
							message: `${
								labelEmployeeID ? labelEmployeeID : 'Employee ID'
							} is required`,
						},
						{
							validator: async (_, value) => {
								// Custom validation logic can be added here if needed
							},
						},
					]}
				>
					<div className="custom-form-group">
						<label className="custom-label">
							{labelEmployeeID ? labelEmployeeID : 'Employee ID'}:
						</label>
						<Input className="custom-input" />
					</div>
				</Form.Item>

				{dontUseDateFormat === false && !state.passedDOB && (
					<Form.Item
						name="dateOfBirth"
						initialValue={state.passedDOB ? state.passedDOB : ''}
						getValueFromEvent={onChangeDate}
						rules={[
							{
								type: 'string',
							},
							{
								validator(_, value) {
									return dayjs(value, 'MM/DD/YYYY', true).isValid()
										? Promise.resolve()
										: Promise.reject(
												'Enter a valid Date of Birth. Use format MM/DD/YYYY.'
											);
								},
							},
						]}
					>
						<div className="custom-form-group">
							<label className="custom-label">{dobLabel}:</label>
							<Input placeholder="MM/DD/YYYY" className="custom-input" />
						</div>
					</Form.Item>
				)}
				{dontUseDateFormat === true && (
					<Form.Item name="dateOfBirth">
						<div className="custom-form-group">
							<label className="custom-label">{dobLabel}:</label>
							<Input className="custom-input" />
						</div>
					</Form.Item>
				)}
				<div className="modal-footer-btn">
					<Button
						type="primary"
						size="large"
						className="btn-min-width"
						htmlType="submit"
					>
						Continue
					</Button>
				</div>
			</Form>
		</Modal>
	);
};

export default withApollo(withRouter(ClaimAccountModal));
